import {
  useMemo,
  useRef,
  useEffect
} from "react";

import {
  axisBottom,
  axisLeft,
  bin,
  select,
  scaleLinear,
} from "d3";
import AnalysisLstatTextWrap from "./AnalysisLstatTextWrap";
import { AnalysisLstatLogsGraphInterface } from "../../../interfaces/fe.interfaces";

export interface AnalysisLstatHistogramProps {
  width: number;
  height: number;
  xDomain: number[];
  data: number[];
  graph: AnalysisLstatLogsGraphInterface
}

const MARGIN = { top: 30, right: 30, bottom: 40, left: 50 };
const BUCKET_PADDING = 1;

export const AnalysisLstatHistogram = ({
  width,
  height,
  xDomain,
  data,
  graph }: AnalysisLstatHistogramProps) => {

  const axesRef = useRef(null);
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const xDomainMinMax = useMemo(() => {
    const max = Math.max(...xDomain);
    const min = Math.min(...xDomain);
    const minMax: [number, number] = [min, max];
    return minMax;
  }, [xDomain]);

  const xScale = useMemo(() => {
    return scaleLinear()
      .domain(xDomainMinMax)
      .range([0, boundsWidth]);
  }, [data, width, xDomainMinMax]);

  const buckets = useMemo(() => {
    const bucketGenerator = bin<number, number>()
      .value((d) => d)
      .domain(xDomainMinMax)
      .thresholds(xDomain);

    const bins = bucketGenerator(data);

    return bins;
  }, [xScale, xDomainMinMax]);

  const yScale = useMemo(() => {
    const max = Math.max(...buckets.map((bucket) => bucket?.length));
    return scaleLinear().range([boundsHeight, 0]).domain([0, max]).nice();
  }, [data, height]);

  // Render the X axis using d3.js, not react
  useEffect(() => {
    const svgElement = select(axesRef.current);
    svgElement.selectAll("*").remove();

    const xAxisGenerator = axisBottom(xScale);
    svgElement
      .append("g")
      .attr("transform", "translate(0," + boundsHeight + ")")
      .call(xAxisGenerator);

    const yAxisGenerator = axisLeft(yScale);
    svgElement.append("g").call(yAxisGenerator);
  }, [xScale, yScale, boundsHeight]);

  const allRects = buckets.map((bucket, i) => {
    return (
      <rect
        key={i}
        fill="#007dba"
        x={xScale(bucket?.x0 || 0) + BUCKET_PADDING / 2}
        width={xScale(bucket.x1 || 0) - xScale(bucket.x0 || 0) - BUCKET_PADDING}
        y={yScale(bucket.length)}
        height={boundsHeight - yScale(bucket.length)}
      />
    );
  });

  return (
    <div>
      <svg width={width} height={height}>
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        >
          {allRects}
        </g>
        <g
          width={boundsWidth}
          height={boundsHeight}
          ref={axesRef}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        />
        <text
          x={((boundsWidth + MARGIN.left + MARGIN.right) / 2)}
          y={(boundsHeight + MARGIN.bottom + MARGIN.top)}
          style={{textAnchor: "middle"}}
        >
          {graph.x_label[0]}
        </text>
        <text
          x={(-height / 2)}
          y={(10)}
          style={{textAnchor: "middle"}}
          transform="rotate(-90)">
          {graph.y_label[0]}
        </text>
        <text
          x={((boundsWidth + MARGIN.left + MARGIN.right) / 2)}
          y={(MARGIN.top - 15)}
          style={{textAnchor: "middle"}}
        >
          {graph.event_category}
        </text>
      </svg>
      <svg
        viewBox={`0,0,${125},${height}`}
        width={125}
        height={height}
      >
        <text x={0} y={0}>

          <tspan
            x={0}
            dy={12}
          >
            Name:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.username}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Version:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.version}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Trial time:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.trial_test_time_date}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Notes:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            <AnalysisLstatTextWrap text={graph.notes} width={125} />
          </tspan>
        </text>
      </svg>
    </div>
  );

};

export default AnalysisLstatHistogram;